import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui';
import type { OpenAITool, Message, ToolCallWithResult } from '../../types';
import { Wrench, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { cn } from '../../utils';

/** Separator the LB uses between MCP server prefix and tool name. */
const SERVER_SEPARATOR = '__';

interface ServerGroup {
  /** Server prefix, e.g. "live-data". */
  prefix: string;
  tools: OpenAITool[];
  /** Total calls made to any tool on this server in the current thread. */
  callCount: number;
  /** True if any tool on this server last ended in error. */
  hasError: boolean;
}

/**
 * Group the flat tool list by MCP server.
 *
 * The LB returns names as `<server>__<tool>` (see /v1/tools), so the prefix is
 * the grouping key. Tools without a separator are unusual but possible — they
 * fall under "other" rather than being dropped, so the dialog can never show
 * fewer tools than the header count claims.
 */
function groupByServer(tools: OpenAITool[], messages: Message[]): ServerGroup[] {
  const groups = new Map<string, OpenAITool[]>();

  for (const tool of tools) {
    const name = tool.function.name;
    const idx = name.indexOf(SERVER_SEPARATOR);
    const prefix = idx > 0 ? name.slice(0, idx) : 'other';
    const list = groups.get(prefix);
    if (list) list.push(tool);
    else groups.set(prefix, [tool]);
  }

  return Array.from(groups.entries())
    .map(([prefix, groupTools]) => {
      let callCount = 0;
      let hasError = false;
      for (const tool of groupTools) {
        const stats = getToolStats(tool.function.name, messages);
        callCount += stats.callCount;
        if (stats.lastStatus === 'error') hasError = true;
      }
      // Sort within the group on the *displayed* (prefix-stripped) name, so the
      // visible order matches — sorting on the raw name would look wrong for any
      // server whose prefix isn't a shared constant length.
      const sorted = [...groupTools].sort((a, b) =>
        shortToolName(a.function.name).localeCompare(
          shortToolName(b.function.name),
        ),
      );
      return { prefix, tools: sorted, callCount, hasError };
    })
    // Always alphabetical, so a server sits in the same place every time you
    // open the dialog. Sorting by usage was tried first, but it moves rows
    // between openings — the list is for looking something up, and a stable
    // position beats surfacing the recently-used group. Errors are still
    // visible via the red group name.
    .sort((a, b) => a.prefix.localeCompare(b.prefix));
}

/** Strip the `<server>__` prefix — it's already the group heading. */
function shortToolName(name: string): string {
  const idx = name.indexOf(SERVER_SEPARATOR);
  return idx > 0 ? name.slice(idx + SERVER_SEPARATOR.length) : name;
}

interface ToolsInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tools: OpenAITool[];
  messages: Message[];
}

interface ToolStats {
  callCount: number;
  lastStatus: ToolCallWithResult['status'] | null;
}

function getToolStats(toolName: string, messages: Message[]): ToolStats {
  let callCount = 0;
  let lastStatus: ToolCallWithResult['status'] | null = null;

  for (const message of messages) {
    if (message.toolCalls) {
      for (const tc of message.toolCalls) {
        if (tc.name === toolName) {
          callCount++;
          lastStatus = tc.status;
        }
      }
    }
  }

  return { callCount, lastStatus };
}

function StatusIndicator({ status }: { status: ToolCallWithResult['status'] | null }) {
  if (!status) {
    return <span className="text-xs text-muted-foreground">Not called</span>;
  }

  switch (status) {
    case 'completed':
      return (
        <div className="flex items-center gap-1 text-green-500">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs">Success</span>
        </div>
      );
    case 'error':
      return (
        <div className="flex items-center gap-1 text-red-500">
          <XCircle className="h-4 w-4" />
          <span className="text-xs">Failed</span>
        </div>
      );
    case 'running':
      return (
        <div className="flex items-center gap-1 text-blue-500">
          <AlertCircle className="h-4 w-4 animate-pulse" />
          <span className="text-xs">Running</span>
        </div>
      );
    case 'pending':
      return (
        <div className="flex items-center gap-1 text-muted-foreground">
          <AlertCircle className="h-4 w-4" />
          <span className="text-xs">Pending</span>
        </div>
      );
    default:
      return null;
  }
}

export function ToolsInfoDialog({
  open,
  onOpenChange,
  tools,
  messages,
}: ToolsInfoDialogProps) {
  const groups = groupByServer(tools, messages);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-500" />
            Connected Tools
          </DialogTitle>
          <DialogDescription>
            {groups.length === 0
              ? 'No MCP servers connected'
              : `${tools.length} tools across ${groups.length} ${
                  groups.length === 1 ? 'server' : 'servers'
                }`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No tools available
            </p>
          ) : (
            // Collapsed by default — 53 tools as a flat list was unreadable, and
            // the server is the level users actually reason about. `multiple`
            // so comparing two servers doesn't collapse the first.
            <Accordion type="multiple" className="pb-4">
              {groups.map((group) => (
                <AccordionItem key={group.prefix} value={group.prefix}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                      <span
                        className={cn(
                          'font-medium text-sm truncate',
                          group.hasError && 'text-red-500',
                        )}
                      >
                        {group.prefix}
                      </span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {group.tools.length}
                      </span>
                      {group.callCount > 0 && (
                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {group.callCount}{' '}
                          {group.callCount === 1 ? 'call' : 'calls'}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-1">
                      {group.tools.map((tool) => {
                        const stats = getToolStats(
                          tool.function.name,
                          messages,
                        );
                        return (
                          <div
                            key={tool.function.name}
                            className={cn(
                              'rounded-lg border p-2.5',
                              stats.lastStatus === 'error' &&
                                'border-red-500/30 bg-red-500/5',
                              stats.lastStatus === 'completed' &&
                                'border-green-500/30 bg-green-500/5',
                              !stats.lastStatus &&
                                'border-border bg-muted/30',
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-sm truncate">
                                    {shortToolName(tool.function.name)}
                                  </h4>
                                  {stats.callCount > 0 && (
                                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full flex-shrink-0">
                                      {stats.callCount}{' '}
                                      {stats.callCount === 1 ? 'call' : 'calls'}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {tool.function.description ||
                                    'No description'}
                                </p>
                              </div>
                              {stats.lastStatus && (
                                <div className="flex-shrink-0">
                                  <StatusIndicator status={stats.lastStatus} />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
